import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Container } from "./style";
import U from "../../assets/U.png";
import { Button, Form, Input, Spin, message } from "antd";
import { useDispatch } from "react-redux";
import { setLoginInfo } from "../../store/features/UserInfo";
import { gql, useMutation } from "@apollo/client";
import { redirectToGoogleSignIn } from "../../utils/cognitoOAuth";
import { useCompleteLogin, useResumingGoogleSignIn } from "./loginQueries";

const LOGIN_MUTATION = gql`
  mutation loginUser($input: LoginUserInput!) {
    login(loginUserInput: $input) {
      refreshToken
      accessToken
      idToken
      user {
        _id
        name
      }
    }
  }
`;

function buildVersionLabel() {
  const d = new Date();
  const MM = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const env = process.env.REACT_APP_CUSTOM_ENV ?? "";
  return `V${MM}${dd}${yy}-${env}`;
}

export default function LoginPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const resumingGoogle = useResumingGoogleSignIn();

  const [loginUser] = useMutation(LOGIN_MUTATION);
  const handleAfterLogin = useCompleteLogin();

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const res = await loginUser({ variables: { input: values } });
      dispatch(setLoginInfo({ ...res.data.login, email: values.email }));
      await handleAfterLogin(res.data.login?.user?._id);
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await redirectToGoogleSignIn();
    } catch (error) {
      console.error("Google sign-in error:", error);
      message.error(error.message || "Google sign-in failed");
      setGoogleLoading(false);
    }
  };

  if (resumingGoogle) {
    return (
      <Container>
        <div className="loginPage" style={{ textAlign: "center" }}>
          <Spin size="large" />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <img src={U} alt="U" className="bg-u" />
      <div className="loginPage">
        <div className="loginLogo">
          <div className="logo1">UVITA</div>
          <div className="logo2">PRO</div>
        </div>
        <div className="loginBox">
          <div className="loginBoxTitle">
            Sign In
            <span className="version">{buildVersionLabel()}</span>
          </div>
          <Form
            name="loginForm"
            layout="vertical"
            size="large"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please input your email!" },
                { type: "email", message: "Please input an email" },
              ]}
            >
              <Input placeholder="Input your email" />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Please input your password!" },
              ]}
            >
              <Input.Password placeholder="Input your password" />
            </Form.Item>
            <div className="flex-between">
              <Link to={`/Register`}>
                <div className="forget-pwd">Register</div>
              </Link>
              <Link to={`/ResetPwd?type=forget`}>
                <div className="forget-pwd">Forgot password</div>
              </Link>
            </div>

            <Form.Item wrapperCol={{ span: 12 }}>
              <Button type="primary" block loading={loading} htmlType="submit">
                Sign In
              </Button>
            </Form.Item>
          </Form>

          <div className="divider-container">
            <div className="divider-line"></div>
            <span className="divider-text">OR</span>
            <div className="divider-line"></div>
          </div>

          <div className="google-login-container">
            <Button
              block
              size="large"
              loading={googleLoading}
              onClick={handleGoogleSignIn}
            >
              Sign in with Google
            </Button>
          </div>
        </div>
      </div>
    </Container>
  );
}
